import React, {useMemo, useRef} from 'react';
import moment from "moment";
import {find, isArray, isNil, lowerCase} from 'lodash';
import makeStyles from "@mui/styles/makeStyles";
import { getConsultantName } from "../../../../../../centralPage/helper";
import {
  Box,
  Grid,
  Stack,
  Tooltip,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import clsx from "clsx";
import { useWidgetStyles } from "../../../../doubleValueWidget";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import { getPatientName } from "../../../../../../bedManagement/bmUtils";
import AppointmentsHeatmapCalendar from "../../../../reusable/heatmapCalendar";
import TruncatedText from "../../../../../../../components/ui/text/truncatedText";
import {useSelector} from 'react-redux';
import {practitionerRolesDict} from '../../../../../../../store/reducers/appointments';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 50,
    display: "flex",
    borderRadius: 7,
    marginBottom: 10,
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paperDarker,
  },
  calendarButton: {
    marginLeft: "auto",
    borderRadius: 5,
    padding: 0,
    backgroundColor: theme.palette.background.paper,

    "& svg": {
      transform: "scale(.9)",
    },
  },
  headerText: {
    fontSize: ".95rem",
    fontWeight: 500,
  },
  consultantName: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: ".85rem",
  },
  sessionHours: {
    borderRadius: 7,
    padding: "3px 5px",
    maxWidth: "fit-content",
    maxHeight: "fit-content",
    marginTop: "auto",
    marginBottom: "auto",
    border: `1.5px solid ${theme.palette.mode === "dark" ? "#FFF" : theme.palette.background.default}`,

    "& .MuiTypography-root": {
      fontSize: ".85rem",
    },
    "&.nextToBeSeen": {
      borderColor:
        theme.palette.mode === "dark"
          ? "#FFFFFF75"
          : theme.palette.background.default,
    },
  },
  nextToBeSeenText: {
    fontSize: ".75rem",
    color: theme.palette.mode === "dark" ? "#FFFFFF75" : "#000",

    "&.patientName": {
      fontWeight: 600,
    },
    "&.appointmentDate": {
      fontSize: "0.85rem",
      lineHeight: ".9rem",
    },
  },
}));

export default function SessionDetails({
  session,
  nextToBeSeen,
  showNextToBeSeen,
  columnHeaderStyles,
}) {
  const classes = useStyles();
  const widgetClasses = useWidgetStyles();

  const practitionerRoles = useSelector(practitionerRolesDict);
  const calendarButtonRef = useRef();

  // Hard coded for now, to handle in future according to figma (option to show/hide)
  const nextToBeSeenOptionEnabled = showNextToBeSeen;

  const patientGender = nextToBeSeen
    ? lowerCase(nextToBeSeen?.patient?.gender)
    : null;

  const assignedPractitionerRole = useMemo(() => {
    if (isArray(practitionerRoles) && session?.practitionerRole) {
      return find(practitionerRoles, { _id: session?.practitionerRole });
    } else {
      return null;
    }
  }, [practitionerRoles, session?.practitionerRole])

  return (
    <Grid container className={classes.root} style={columnHeaderStyles}>
      {/*{session?.assignedSpecialty && !isNil(session?.specialty) && (*/}
        <Grid item xs={12} sx={{ p: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            flexWrap="nowrap"
          >
            {session?.sessionName && (
                <TruncatedText
                    text={session?.sessionName}
                    className={classes.headerText}
                />
            )}

            {session?.clinic?.clinicCode && (
                <Tooltip placement="top" title={session?.clinic?.description}>
                  <Typography variant="body2" fontWeight={600}>
                    {`[${session?.clinic?.clinicCode}]`}
                  </Typography>
                </Tooltip>
            )}


            <Box style={{ marginLeft: "auto" }}>
              <AppointmentsHeatmapCalendar
                ref={calendarButtonRef}
                button={
                  <IconButton
                    className={classes.calendarButton}
                    ref={calendarButtonRef}
                    color="primary"
                  >
                    <CalendarMonthIcon />
                  </IconButton>
                }
                selectedClinic={null}
                selectedSession={session}
                hideButton={true}
              />
            </Box>
          </Stack>
        </Grid>
      {/*)}*/}

      <Grid
        container
        item
        xs={12}
        sx={{ p: 1, pt: 0 }}
        flexWrap='nowrap'
        alignItems="center"
        gap={1}
        justifyContent="space-between"
        className='scroll-normal-white'
      >

        <Stack direction='row' alignItems='center' gap={1.5}>
          {session?.practitioner && session?.consultant && (
              <TruncatedText
                  variant="subtitle2"
                  text={session?.practitioner && session?.consultant && getConsultantName(session?.consultant)}
                  className={classes.consultantName}
              />
          )}

          {assignedPractitionerRole?.name && (
              <TruncatedText
                  variant="subtitle2"
                  className={classes.consultantName}
                  text={`[${assignedPractitionerRole?.name}]`}
              />
          )}
        </Stack>

        {session.startDate && session.endDate && (
          <div className={classes.sessionHours}>
            <TruncatedText
              text={`${moment(session.startDate).format("HH:mm")} - ${moment(session.endDate).format("HH:mm")}`}
            />
          </div>
        )}
      </Grid>

      {nextToBeSeenOptionEnabled && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent="space-between"
            sx={{ p: 1, height: 50 }}
          >
            {nextToBeSeen && nextToBeSeenOptionEnabled && (
              <Stack>
                <Box>
                  <TruncatedText
                    text={getPatientName(nextToBeSeen)}
                    className={clsx(classes.nextToBeSeenText, "patientName")}
                  />
                </Box>

                <Stack direction="row" gap={1} alignItems="center">
                  {nextToBeSeen?.patient?.dob && (
                    <TruncatedText
                      text={`Age ${moment().diff(moment(nextToBeSeen?.patient?.dob), "years")};`}
                      className={classes.nextToBeSeenText}
                    />
                  )}

                  {nextToBeSeen?.patient?.hospitalNumber && (
                    <TruncatedText
                      className={classes.nextToBeSeenText}
                      text={nextToBeSeen?.patient?.hospitalNumber}
                    />
                  )}

                  {patientGender && (
                    <div className={clsx(widgetClasses.gender, patientGender)}>
                      {patientGender === "male" ? (
                        <MaleIcon fontSize="small" />
                      ) : (
                        <FemaleIcon fontSize="small" />
                      )}
                    </div>
                  )}
                </Stack>
              </Stack>
            )}

            {nextToBeSeen && nextToBeSeen?.startDate && (
              <Box className={clsx(classes.sessionHours, "nextToBeSeen")}>
                <Typography
                  variant="body2"
                  className={clsx(classes.nextToBeSeenText, "appointmentDate")}
                >
                  {moment(nextToBeSeen?.startDate).format("HH:mm")}
                </Typography>
              </Box>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
}
